import { where } from "firebase/firestore";
import { subscribeToCol, unsubscribeCollection } from "@/composables/events/handler";
import connectorService from "@/services/connectorService";
import { novologikClient } from "@/services/novologikClient";
import { parseIdentity } from "@/store/connector";

const TYPE = "novologik";

/**
 * Sets up the Novologik connector.
 *
 * Listens to "connectors.ready" / "connectors.unready" bus events
 * and manages its own lifecycle:
 *   ready  → subscribe to connector-sessions → get/create session → connect SignalR
 *   unready → unsubscribe + disconnect
 *
 * Registers itself into the connectors store so the generic
 * gotoPatientInConnector action can use it.
 *
 * @param {import('vuex').Store} store - Vuex store instance
 * @param {Vue} bus - Vue event bus
 */
export function setupNovologikConnector(store, bus) {
	let currentJwt = null;
	let isSessionConnecting = false;

	// --- SignalR client (transport layer) ---
	const realtimeClient = novologikClient({
		onCallRequest: (fullName, phone) => {
			bus.$emit("start-outbound-call", {
				phoneNumber: phone,
				patient: fullName ? { firstName: fullName } : null
			});
		},
		onConnected: async () => {
			const { upp, identifier } = getCredentials();

			if (upp || identifier) {
				await realtimeClient.registerGuestApp(upp, identifier);
			}

			store.commit("connector/setConnected", { type: TYPE, connected: true });
		},
		onDisconnected: () => {
			store.commit("connector/setConnected", { type: TYPE, connected: false });
		},
		onReconnected: async () => {
			const { upp, identifier } = getCredentials();

			await realtimeClient.registerGuestApp(upp, identifier);
		}
	});

	// --- Helpers ---

	function getCredentials() {
		const saved = store.getters["connector/connectorCredentials"](TYPE);

		return {
			upp: saved.crmId || "",
			identifier: saved.identifier || ""
		};
	}

	// --- Controller interface (used by connector store) ---

	async function start() {
		const organizationId = store.getters["clinic/clinicData"]?.id;

		if (!organizationId) {
			console.error("[NOVOLOGIK] Cannot start connector: no organization ID");
			return;
		}

		subscribeToCol("connector-sessions", [
			where("organizationId", "==", organizationId),
			where("type", "==", TYPE),
			where("expiresAt", ">", new Date())
		], {
			onInitialLoad: handleInitialLoad,
			onAdded: handleSessionChange,
			onUpdated: handleSessionChange,
			onRemoved: createNewSession, // expired or deleted session
			onUnsubscribed: () => {},
			onError: (error) => {
				console.error("[NOVOLOGIK-EVENTS] Subscription error:", {
					code: error.code,
					message: error.message
				});
			}
		});
	}

	async function stop() {
		unsubscribeCollection("connector-sessions");
		await disconnect();
	}

	function isConnected() {
		return realtimeClient.isConnected();
	}

	async function gotoPatient(identity, fullName, phone) {
		const { upp } = getCredentials();

		return realtimeClient.gotoPatient(upp, identity, fullName, phone);
	}

	// --- Firestore session handlers ---

	async function handleInitialLoad(snapshot) {
		if (snapshot.docs.length === 0) {
			await createNewSession();
			return;
		}

		const [doc] = snapshot.docs;

		await handleSession({ id: doc.id, ...doc.data() });
	}

	async function handleSessionChange(session) {
		await handleSession(session);
	}

	async function createNewSession() {
		try {
			await connectorService.createSession(TYPE);
		} catch (error) {
			console.error("[NOVOLOGIK] Failed to create session:", error);
		}
	}

	async function handleSession(session) {
		if (!session) return;
		if (!session.jwt) return;
		if (isSessionConnecting) return;
		if (currentJwt === session.jwt && realtimeClient.isConnected()) return;

		isSessionConnecting = true;

		try {
			if (realtimeClient.isConnected()) {
				await disconnect();
			}

			currentJwt = session.jwt;

			store.commit("connector/setCredentials", {
				type: TYPE,
				credentials: { crmId: session.crmId || "" }
			});

			await realtimeClient.connect(session.jwt);
		} finally {
			isSessionConnecting = false;
		}
	}

	async function disconnect() {
		await realtimeClient.disconnect();
		currentJwt = null;
	}

	// --- Register into connector store ---

	store.dispatch("connector/register", {
		type: TYPE,
		name: "Novologik",
		controller: { start, stop, isConnected, gotoPatient }
	});

	// --- Listen for connector lifecycle events ---

	bus.$on("connectors.ready", async ({ } = {}) => {
		const raw = $cookies.get("appIdentity") || "";
		const { type, identifier } = parseIdentity(raw);

		if (type !== TYPE || !identifier) return;

		await store.dispatch("connector/start", {
			type: TYPE,
			credentials: { identifier }
		});
	});

	bus.$on("connectors.unready", async () => {
		await store.dispatch("connector/stop", { type: TYPE });
	});
}
